import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import KeyboardVoiceIcon from '@mui/icons-material/KeyboardVoice';
import StopIcon from '@mui/icons-material/Stop';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import SendIcon from '@mui/icons-material/Send';
import RestoreIcon from '@mui/icons-material/Restore';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import NotesIcon from '@mui/icons-material/Notes';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import AudioFileIcon from '@mui/icons-material/AudioFile';
import SettingsVoiceIcon from '@mui/icons-material/SettingsVoice';
import TuneIcon from '@mui/icons-material/Tune';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useChatSessionsContext } from '../../lib/contexts/ChatSessionsContext';
import { useVoiceTurns } from '../../lib/hooks/useVoiceTurns';
import { getPersonas, type Persona } from '../../lib/api/Persona';
import { getPersonaDisplayName } from '../../lib/helpers/personaDisplay';
import { useUserInfoContext } from '../../lib/contexts/UserInfoContext';
import { canAccessPersona } from '@common/domain/auth';

type TimelineItem = {
  id: string;
  role: 'user' | 'assistant';
  kind: 'text' | 'voice-note';
  text?: string;
  transcript?: string;
  audioUrl?: string;
  fileName?: string;
  timestamp?: string;
  isStreaming?: boolean;
};

type PlaybackMode = 'off' | 'assistant-only' | 'all';
type ContentViewMode = 'text' | 'audio' | 'both';

const formatTimestamp = (value?: string) => {
  if (!value || value === 'draft-assistant') return 'Just now';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Just now';
  return date.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const formatDuration = (durationMs?: number) => {
  if (!durationMs || durationMs <= 0) return undefined;
  const totalSeconds = Math.round(durationMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

const StatusChip: React.FC<{ status: string }> = ({ status }) => {
  if (status === 'recording') {
    return <Chip color="error" icon={<KeyboardVoiceIcon />} label="Recording" />;
  }
  if (status === 'ready') {
    return <Chip color="info" icon={<AudioFileIcon />} label="Ready" />;
  }
  if (status === 'sending') {
    return <Chip color="warning" icon={<GraphicEqIcon />} label="Processing" />;
  }
  if (status === 'completed') {
    return <Chip color="success" icon={<CheckCircleOutlineIcon />} label="Completed" />;
  }
  if (status === 'error') {
    return <Chip color="error" icon={<ErrorOutlineIcon />} label="Error" />;
  }
  return <Chip variant="outlined" label="Idle" />;
};

const WaveBars: React.FC<{ active?: boolean }> = ({ active = false }) => {
  const bars = [14, 22, 30, 18, 34, 20, 26, 16, 28, 24, 18, 32];
  return (
    <Stack direction="row" alignItems="end" spacing={0.4} sx={{ height: 36 }}>
      {bars.map((h, i) => (
        <Box
          key={`${h}-${i}`}
          sx={{
            width: 4,
            height: h,
            borderRadius: 999,
            bgcolor: 'primary.main',
            opacity: 0.85,
            animation: active ? `barsPulse 1.1s ease-in-out ${i * 0.08}s infinite` : 'none',
            '@keyframes barsPulse': {
              '0%': { transform: 'scaleY(0.7)' },
              '50%': { transform: 'scaleY(1)' },
              '100%': { transform: 'scaleY(0.7)' },
            },
            transformOrigin: 'bottom center',
          }}
        />
      ))}
    </Stack>
  );
};

const AudioNotesPage: React.FC = () => {
  const {
    persona: selectedPersona,
    updatePersona,
    selectedChatSession,
    messages: globalMessages,
  } = useChatSessionsContext();

  const { userInfo } = useUserInfoContext();
  const { state, selectFile, startRecording, stopRecording, sendSelectedNote, reset } = useVoiceTurns();

  const [manualChatId, setManualChatId] = useState('');
  const [pageError, setPageError] = useState<string>();
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [selectedFilePreviewUrl, setSelectedFilePreviewUrl] = useState<string>();

  const [showTranscripts, setShowTranscripts] = useState(true);
  const [showChatText, setShowChatText] = useState(true);
  const [showAudioPlayers, setShowAudioPlayers] = useState(true);
  const [readMessagesAloud, setReadMessagesAloud] = useState(false);
  const [autoPlayNewAssistantAudio, setAutoPlayNewAssistantAudio] = useState(false);
  const [playbackMode, setPlaybackMode] = useState<PlaybackMode>('assistant-only');
  const [contentViewMode, setContentViewMode] = useState<ContentViewMode>('both');
  const [speechRate, setSpeechRate] = useState(1);
  const [currentlySpeakingId, setCurrentlySpeakingId] = useState<string | null>(null);
  const lastAutoPlayedIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!userInfo) return;

    getPersonas(userInfo)
      .then((list) => {
        const filtered = list.filter(p => canAccessPersona(p, userInfo));
        setPersonas(filtered);
        if (!selectedPersona && filtered.length > 0) {
          const defaultPersona = filtered.find((p) => p.id === 'p072690a2e4b') || filtered[0];
          updatePersona(defaultPersona);
        }
      })
      .catch((err) => console.error('Failed to fetch personas:', err));
  }, [userInfo, selectedPersona, updatePersona]);

  useEffect(() => {
    if (!state.selectedFile) {
      setSelectedFilePreviewUrl(undefined);
      return;
    }
    const objectUrl = URL.createObjectURL(state.selectedFile);
    setSelectedFilePreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [state.selectedFile]);

  useEffect(() => {
    return () => {
      if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  const effectiveChatId = useMemo(
    () => manualChatId.trim() || selectedChatSession || undefined,
    [manualChatId, selectedChatSession],
  );

  const historyItems = useMemo<TimelineItem[]>(() => {
    return globalMessages.map((msg, index) => ({
      id: msg.exchangeId || `history-${index}`,
      role: msg.fromUser ? 'user' : 'assistant',
      kind: msg.messageType === 'voice-note' || msg.audioUrl || msg.transcript ? 'voice-note' : 'text',
      text: msg.message,
      transcript: msg.transcript,
      audioUrl: msg.audioUrl,
      fileName: msg.fileName,
      timestamp: msg.timestamp,
      isStreaming: msg.isStreaming,
    }));
  }, [globalMessages]);

  const draftItems = useMemo<TimelineItem[]>(() => {
    const items: TimelineItem[] = [];

    if (state.selectedFile) {
      items.push({
        id: state.turnId ? `user-${state.turnId}` : 'user-draft',
        role: 'user',
        kind: 'voice-note',
        transcript: state.transcript,
        fileName: state.selectedFile.name,
        timestamp: new Date().toISOString(),
      });
    }

    const hasAssistant = state.streamedAssistantText || state.finalAssistantText || state.replayAudioUrl;
    if (hasAssistant) {
      items.push({
        id: state.turnId ? `assistant-${state.turnId}` : 'assistant-draft',
        role: 'assistant',
        kind: state.replayAudioUrl ? 'voice-note' : 'text',
        text: state.finalAssistantText || state.streamedAssistantText,
        audioUrl: state.replayAudioUrl,
        timestamp: 'draft-assistant',
        isStreaming: state.status === 'sending' && !state.finalAssistantText,
      });
    }

    return items;
  }, [
    state.selectedFile,
    state.transcript,
    state.streamedAssistantText,
    state.finalAssistantText,
    state.replayAudioUrl,
    state.turnId,
    state.status,
  ]);

  const combinedTimeline = useMemo(() => {
    return [...historyItems, ...draftItems].sort((a, b) => {
      const aTime = a.timestamp ? new Date(a.timestamp).getTime() : 0;
      const bTime = b.timestamp ? new Date(b.timestamp).getTime() : 0;
      return aTime - bTime;
    });
  }, [historyItems, draftItems]);

  const transcriptCount = useMemo(
    () => combinedTimeline.filter((item) => Boolean(item.transcript)).length,
    [combinedTimeline],
  );

  const voiceCount = useMemo(
    () => combinedTimeline.filter((item) => item.kind === 'voice-note').length,
    [combinedTimeline],
  );

  const latestAssistantItem = useMemo(() => {
    const items = [...combinedTimeline].reverse();
    return items.find((item) => item.role === 'assistant');
  }, [combinedTimeline]);

  const getStatusText = () => {
    switch (state.status) {
      case 'recording':
        return 'Recording in progress...';
      case 'ready':
        return 'Voice note ready to review and send.';
      case 'sending':
        return 'Sending and processing voice note...';
      case 'completed':
        return 'Response complete.';
      case 'error':
        return 'Something went wrong.';
      default:
        return 'Record or upload a voice note to begin.';
    }
  };

  const stopSpeaking = () => {
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }
    setCurrentlySpeakingId(null);
  };

  const speakMessage = (item: TimelineItem) => {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) return;

    const textToSpeak = item.text || item.transcript;
    if (!textToSpeak) return;

    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(textToSpeak);
    utterance.rate = speechRate;
    utterance.onend = () => setCurrentlySpeakingId(null);
    utterance.onerror = () => setCurrentlySpeakingId(null);
    setCurrentlySpeakingId(item.id);
    window.speechSynthesis.speak(utterance);
  };

  useEffect(() => {
    if (!readMessagesAloud || !latestAssistantItem) return;
    if (playbackMode === 'off') return;
    if (playbackMode === 'all' || latestAssistantItem.role === 'assistant') {
      if (lastAutoPlayedIdRef.current !== latestAssistantItem.id) {
        lastAutoPlayedIdRef.current = latestAssistantItem.id;
        speakMessage(latestAssistantItem);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latestAssistantItem, readMessagesAloud, playbackMode, speechRate]);

  const handleSend = async () => {
    if (!selectedPersona?.id) {
      setPageError('Select a persona before sending a voice note.');
      return;
    }

    setPageError(undefined);

    try {
      await sendSelectedNote({
        personaId: selectedPersona.id,
        chatId: effectiveChatId,
      });
    } catch (error) {
      setPageError(error instanceof Error ? error.message : 'Failed to send voice note.');
    }
  };

  const shouldRenderText = (item: TimelineItem) => {
    if (contentViewMode === 'audio') return false;
    return showChatText && Boolean(item.text);
  };

  const shouldRenderAudio = (item: TimelineItem) => {
    if (contentViewMode === 'text') return false;
    return showAudioPlayers && Boolean(item.audioUrl);
  };

  const shouldRenderTranscript = (item: TimelineItem) => {
    return showTranscripts && Boolean(item.transcript);
  };

  return (
    <Box sx={{ maxWidth: 1380, mx: 'auto', px: { xs: 2, md: 3 }, py: 3.5 }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h4" sx={{ fontFamily: "'Newsreader', Georgia, serif", mb: 1 }}>
            Audio Notes
          </Typography>
          <Typography color="text.secondary" sx={{ maxWidth: 980 }}>
            Unified conversation history for audio and chat, with transcript visibility, text-to-speech,
            playback controls, and per-session audio behavior settings.
          </Typography>
        </Box>

        {pageError && <Alert severity="error">{pageError}</Alert>}
        {state.error && <Alert severity="error">{state.error}</Alert>}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', xl: '390px 1fr' },
            gap: 3,
            alignItems: 'start',
          }}
        >
          <Stack spacing={3} sx={{ position: { xl: 'sticky' }, top: { xl: 24 } }}>
            <Card variant="outlined" sx={{ borderRadius: 4, overflow: 'hidden' }}>
              <Box
                sx={{
                  px: 2.5,
                  py: 2,
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05),
                  borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="h6">Composer</Typography>
                  <StatusChip status={state.status} />
                </Stack>
              </Box>

              <CardContent>
                <Stack spacing={2.5}>
                  <Typography variant="body2" color="text.secondary">
                    Record or upload audio, then send it into the same session timeline as text chat.
                  </Typography>

                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip icon={<ChatBubbleOutlineIcon />} label={`Chat: ${effectiveChatId || 'new / selected session'}`} />
                    <Chip label={`Recording: ${state.recordingSupported ? 'supported' : 'unsupported'}`} />
                    <Chip label={`Buffered chunks: ${state.livePlayback.bufferedChunkCount}`} />
                  </Stack>

                  <TextField
                    label="Active Chat ID"
                    value={manualChatId}
                    onChange={(event) => setManualChatId(event.target.value)}
                    placeholder={selectedChatSession || 'Reuse selected session or leave blank'}
                    fullWidth
                  />

                  <TextField
                    select
                    label="Persona"
                    value={selectedPersona?.id || ''}
                    onChange={(e) => {
                      const p = personas.find((persona) => persona.id === e.target.value);
                      if (p) updatePersona(p);
                    }}
                    fullWidth
                  >
                    {[...personas]
                      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                      .map((persona) => (
                        <MenuItem key={persona.id} value={persona.id}>
                          {getPersonaDisplayName(persona)}
                        </MenuItem>
                      ))}
                  </TextField>

                  <Stack spacing={1.25}>
                    <Button variant="outlined" component="label" startIcon={<UploadFileIcon />} fullWidth>
                      Select Audio File
                      <input
                        hidden
                        type="file"
                        accept="audio/*,video/webm"
                        onChange={(event) => selectFile(event.target.files?.[0] || null)}
                      />
                    </Button>

                    <Button
                      variant="outlined"
                      startIcon={<KeyboardVoiceIcon />}
                      disabled={!state.recordingSupported || state.status === 'recording' || state.status === 'sending'}
                      onClick={() => startRecording().catch((error) => setPageError(error.message))}
                      fullWidth
                    >
                      Start Recording
                    </Button>

                    <Button
                      variant="outlined"
                      color="warning"
                      startIcon={<StopIcon />}
                      disabled={state.status !== 'recording'}
                      onClick={() => stopRecording().catch((error) => setPageError(error.message))}
                      fullWidth
                    >
                      Stop Recording
                    </Button>

                    <Button
                      variant="contained"
                      startIcon={<SendIcon />}
                      disabled={!state.selectedFile || state.status === 'recording' || state.status === 'sending'}
                      onClick={handleSend}
                      fullWidth
                    >
                      Send Voice Note
                    </Button>

                    <Button
                      variant="text"
                      startIcon={<RestoreIcon />}
                      disabled={state.status === 'sending'}
                      onClick={() => {
                        setPageError(undefined);
                        reset();
                      }}
                      fullWidth
                    >
                      Reset
                    </Button>
                  </Stack>

                  {selectedFilePreviewUrl && (
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 3,
                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05),
                        border: (theme) => `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      <Stack spacing={1.5}>
                        <Stack direction="row" spacing={1.25} alignItems="center">
                          <AudioFileIcon color="primary" />
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="subtitle2" noWrap>
                              {state.selectedFile?.name || 'Selected file'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {formatDuration(state.selectedFileDurationMs) || 'Duration unavailable'}
                            </Typography>
                          </Box>
                        </Stack>
                        <WaveBars active={state.status === 'recording'} />
                        <audio controls src={selectedFilePreviewUrl} style={{ width: '100%' }} />
                      </Stack>
                    </Box>
                  )}

                  <Divider />

                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TuneIcon fontSize="small" />
                      <Typography variant="subtitle1">Session audio controls</Typography>
                    </Stack>

                    <FormControlLabel
                      control={<Switch checked={showTranscripts} onChange={(e) => setShowTranscripts(e.target.checked)} />}
                      label="Show transcript text"
                    />
                    <FormControlLabel
                      control={<Switch checked={showChatText} onChange={(e) => setShowChatText(e.target.checked)} />}
                      label="Show message text"
                    />
                    <FormControlLabel
                      control={<Switch checked={showAudioPlayers} onChange={(e) => setShowAudioPlayers(e.target.checked)} />}
                      label="Show audio players"
                    />
                    <FormControlLabel
                      control={<Switch checked={readMessagesAloud} onChange={(e) => setReadMessagesAloud(e.target.checked)} />}
                      label="Read messages aloud in this chat session"
                    />
                    <FormControlLabel
                      control={<Switch checked={autoPlayNewAssistantAudio} onChange={(e) => setAutoPlayNewAssistantAudio(e.target.checked)} />}
                      label="Autoplay new assistant audio when available"
                    />

                    <FormControl fullWidth size="small">
                      <InputLabel id="playback-mode-label">Read aloud mode</InputLabel>
                      <Select
                        labelId="playback-mode-label"
                        value={playbackMode}
                        label="Read aloud mode"
                        onChange={(e) => setPlaybackMode(e.target.value as PlaybackMode)}
                      >
                        <MenuItem value="off">Off</MenuItem>
                        <MenuItem value="assistant-only">Assistant only</MenuItem>
                        <MenuItem value="all">All messages</MenuItem>
                      </Select>
                    </FormControl>

                    <FormControl fullWidth size="small">
                      <InputLabel id="content-view-mode-label">Conversation view</InputLabel>
                      <Select
                        labelId="content-view-mode-label"
                        value={contentViewMode}
                        label="Conversation view"
                        onChange={(e) => setContentViewMode(e.target.value as ContentViewMode)}
                      >
                        <MenuItem value="text">Text only</MenuItem>
                        <MenuItem value="audio">Audio only</MenuItem>
                        <MenuItem value="both">Text + audio</MenuItem>
                      </Select>
                    </FormControl>

                    <Box>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                        <Typography variant="body2">Speech speed</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {speechRate.toFixed(2)}x
                        </Typography>
                      </Stack>
                      <Slider
                        min={0.5}
                        max={2}
                        step={0.05}
                        value={speechRate}
                        onChange={(_, value) => setSpeechRate(value as number)}
                      />
                    </Box>

                    {currentlySpeakingId ? (
                      <Button variant="outlined" color="inherit" startIcon={<PauseIcon />} onClick={stopSpeaking}>
                        Stop reading aloud
                      </Button>
                    ) : null}
                  </Stack>

                  <Divider />

                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      bgcolor: 'background.default',
                      border: (theme) => `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
                      Current status
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {getStatusText()}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>

            <Card variant="outlined" sx={{ borderRadius: 4 }}>
              <CardContent>
                <Stack spacing={1.5}>
                  <Typography variant="h6">Unified Session Summary</Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip label={`Messages: ${combinedTimeline.length}`} />
                    <Chip label={`Voice: ${voiceCount}`} />
                    <Chip label={`Transcripts: ${transcriptCount}`} />
                    <Chip label={`Persona: ${getPersonaDisplayName(selectedPersona) || 'None'}`} />
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    This summary is based on the combined chat and audio timeline shown on the right.
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          </Stack>

          <Card variant="outlined" sx={{ borderRadius: 4, minHeight: 760, overflow: 'hidden' }}>
            <Box
              sx={{
                px: 3,
                py: 2.25,
                borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                bgcolor: (theme) => alpha(theme.palette.background.default, 0.85),
              }}
            >
              <Stack
                direction={{ xs: 'column', lg: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', lg: 'center' }}
                spacing={1.5}
              >
                <Box>
                  <Typography variant="h6">Combined Chat + Audio History</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Text messages, transcripts, generated audio, and uploaded voice notes appear in one thread.
                  </Typography>
                </Box>

                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip size="small" icon={<NotesIcon />} label={showTranscripts ? 'Transcripts on' : 'Transcripts off'} />
                  <Chip size="small" icon={<SettingsVoiceIcon />} label={readMessagesAloud ? 'Read aloud on' : 'Read aloud off'} />
                  <Chip size="small" icon={showAudioPlayers ? <VolumeUpIcon /> : <VolumeOffIcon />} label={showAudioPlayers ? 'Audio on' : 'Audio hidden'} />
                </Stack>
              </Stack>
            </Box>

            <Box
              sx={{
                p: { xs: 2, md: 3 },
                bgcolor: (theme) => alpha(theme.palette.background.default, 0.45),
                minHeight: 680,
              }}
            >
              {combinedTimeline.length === 0 ? (
                <Box
                  sx={{
                    minHeight: 560,
                    display: 'grid',
                    placeItems: 'center',
                    textAlign: 'center',
                    px: 2,
                  }}
                >
                  <Box sx={{ maxWidth: 420 }}>
                    <GraphicEqIcon sx={{ fontSize: 36, mb: 2, color: 'primary.main' }} />
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      No conversation yet
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Upload or record a voice note to start the unified session timeline.
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Stack spacing={2.5}>
                  {combinedTimeline.map((item) => {
                    const isAssistant = item.role === 'assistant';
                    const bubbleBg = isAssistant ? 'background.paper' : 'primary.main';
                    const bubbleColor = isAssistant ? 'text.primary' : 'primary.contrastText';
                    const textToRead = item.text || item.transcript;

                    return (
                      <Stack
                        key={item.id}
                        direction="row"
                        spacing={1.5}
                        justifyContent={isAssistant ? 'flex-start' : 'flex-end'}
                        alignItems="flex-start"
                      >
                        {isAssistant && (
                          <Box
                            sx={{
                              width: 38,
                              height: 38,
                              borderRadius: '50%',
                              display: 'grid',
                              placeItems: 'center',
                              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
                              color: 'primary.main',
                              flexShrink: 0,
                              mt: 0.5,
                            }}
                          >
                            <SmartToyIcon fontSize="small" />
                          </Box>
                        )}

                        <Box sx={{ maxWidth: { xs: '100%', md: '78%' }, minWidth: { xs: '74%', md: '42%' } }}>
                          <Box
                            sx={{
                              px: 2,
                              py: 1.6,
                              borderRadius: 3,
                              bgcolor: bubbleBg,
                              color: bubbleColor,
                              border: isAssistant ? (theme) => `1px solid ${theme.palette.divider}` : 'none',
                              boxShadow: 1,
                            }}
                          >
                            <Stack spacing={1.25}>
                              <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                                <Box>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                    {isAssistant ? selectedPersona?.name || 'Assistant' : 'You'}
                                  </Typography>
                                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                    {item.kind === 'voice-note' ? item.fileName || 'Voice note' : 'Text message'}
                                  </Typography>
                                </Box>

                                <Stack direction="row" spacing={1} alignItems="center">
                                  {item.kind === 'voice-note' && <Chip size="small" label="Voice" icon={<GraphicEqIcon />} />}
                                  {item.isStreaming && <Chip size="small" label="Streaming" />}
                                  {textToRead ? (
                                    <Tooltip title={currentlySpeakingId === item.id ? 'Stop reading' : 'Read this message'}>
                                      <IconButton
                                        size="small"
                                        onClick={() => {
                                          if (currentlySpeakingId === item.id) {
                                            stopSpeaking();
                                          } else {
                                            speakMessage(item);
                                          }
                                        }}
                                        sx={{
                                          color: isAssistant ? 'inherit' : 'primary.contrastText',
                                          border: isAssistant ? '1px solid rgba(0,0,0,0.08)' : '1px solid rgba(255,255,255,0.24)',
                                        }}
                                      >
                                        {currentlySpeakingId === item.id ? <PauseIcon fontSize="small" /> : <VolumeUpIcon fontSize="small" />}
                                      </IconButton>
                                    </Tooltip>
                                  ) : null}
                                </Stack>
                              </Stack>

                              {shouldRenderText(item) && item.text && (
                                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                  {item.text}
                                </Typography>
                              )}

                              {shouldRenderTranscript(item) && item.transcript && (
                                <Box
                                  sx={{
                                    p: 1.5,
                                    borderRadius: 2,
                                    bgcolor: isAssistant
                                      ? (theme) => alpha(theme.palette.text.primary, 0.03)
                                      : 'rgba(255,255,255,0.16)',
                                    border: isAssistant
                                      ? (theme) => `1px solid ${theme.palette.divider}`
                                      : '1px solid rgba(255,255,255,0.16)',
                                  }}
                                >
                                  <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontWeight: 700, opacity: 0.82 }}>
                                    Transcript
                                  </Typography>
                                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                    {item.transcript}
                                  </Typography>
                                </Box>
                              )}

                              {shouldRenderAudio(item) && item.audioUrl && (
                                <Box>
                                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                      Audio playback
                                    </Typography>
                                    <Tooltip title="Play the generated audio response">
                                      <IconButton
                                        size="small"
                                        onClick={() => {
                                          const element = document.getElementById(`audio-${item.id}`) as HTMLAudioElement | null;
                                          if (!element) return;
                                          if (element.paused) {
                                            void element.play();
                                          } else {
                                            element.pause();
                                          }
                                        }}
                                        sx={{
                                          color: isAssistant ? 'inherit' : 'primary.contrastText',
                                          border: isAssistant ? '1px solid rgba(0,0,0,0.08)' : '1px solid rgba(255,255,255,0.24)',
                                        }}
                                      >
                                        <PlayArrowIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </Stack>
                                  <audio
                                    id={`audio-${item.id}`}
                                    controls
                                    autoPlay={Boolean(autoPlayNewAssistantAudio && isAssistant && latestAssistantItem?.id === item.id)}
                                    src={item.audioUrl}
                                    style={{ width: '100%' }}
                                  />
                                </Box>
                              )}
                            </Stack>
                          </Box>

                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', mt: 0.75, px: 0.5, textAlign: isAssistant ? 'left' : 'right' }}
                          >
                            {formatTimestamp(item.timestamp)}
                          </Typography>
                        </Box>

                        {!isAssistant && (
                          <Box
                            sx={{
                              width: 38,
                              height: 38,
                              borderRadius: '50%',
                              display: 'grid',
                              placeItems: 'center',
                              bgcolor: (theme) => alpha(theme.palette.secondary.main, 0.12),
                              color: 'secondary.main',
                              flexShrink: 0,
                              mt: 0.5,
                            }}
                          >
                            <GraphicEqIcon fontSize="small" />
                          </Box>
                        )}
                      </Stack>
                    );
                  })}
                </Stack>
              )}
            </Box>
          </Card>
        </Box>
      </Stack>
    </Box>
  );
};

export default AudioNotesPage;
